/* eslint-disable no-undef */

import React, { useEffect, useState, Suspense, useContext } from 'react'

import { ref, onValue } from 'firebase/database'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import { CheckForColdSale, IsWorking } from '@kolayyolla/common/services/venueService'
import { GetSettings } from '@kolayyolla/common/store/actions'
import { GetProductTypes } from '@kolayyolla/common/store/actions/menu/productTypes'
import { GetTables, SetTable } from '@kolayyolla/common/store/actions/table'
import { GetVenue } from '@kolayyolla/common/store/actions/venue'
import { b64DecodeUnicode } from '@kolayyolla/common/utils/helpers'

import { Backdrop, CircularProgress, Skeleton } from '@mui/material'
import { makeStyles } from '@mui/styles'

import Topbar from '../../components/Topbar'
import { firebaseAuth, firebaseDatabase } from '../../firebaseApp'
import MenuContext from '../../MenuContext'
import ThemeContext from '../../ThemeContext'

import '@sweetalert2/theme-material-ui/material-ui.css'

const MenuContainer = React.lazy(() => import('./Menu'))

const useStyles = makeStyles((theme) => {
  const classList = {
    backdrop: {
      fontFamily: theme.typography.fontFamily,
      zIndex: theme.zIndex.drawer + 1,
      color: '#fff',
      '& > div': {
        textAlign: 'center',
        '& p': {
          margin: theme.spacing(5)
        }
      }
    },
    themeFont: {
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.fontSize
    }
  }
  return classList
})

const Main = () => {
  const classes                   = useStyles()
  const dispatch                  = useDispatch()
  const { alias, tableID, resId } = useParams()
  const navigate                  = useNavigate()
  const { changeTheme }           = useContext(ThemeContext)

  const venue        = useSelector((state) => state.venue)
  const user         = useSelector((state) => state.user)
  const table        = useSelector((state) => state.table)
  const settings     = useSelector((state) => state.settings)
  const productTypes = useSelector((state) => state.menu.productTypes)

  const [isVenueOnline, setIsVenueOnline]                       = useState(false)
  const [isPackageOrder, setIsPackageOrder]                     = useState(true)
  const [foundHotSaleDeliveryArea, setFoundHotSaleDeliveryArea] = useState(false
  )
  const [isColdSaleActive, setIsColdSaleActive]                 = useState(false)
  const [isWithInColdSaleLimits, setIsWithInColdSaleLimits]     = useState(false)
  const [isTemporarilyUnavailable, setIsTemporarilyUnavailable] = useState(false
  )
  const [isAdminOnline, setIsAdminOnline]                       = useState(false)

  useEffect(() => {
    if (!settings.isLoaded) {
      if (firebaseAuth?.currentUser) {
        firebaseAuth.currentUser.getIdToken()
          .then((token) => {
            const payload = JSON.parse(b64DecodeUnicode(token.split('.')[1]))
            setIsAdminOnline(
              payload.role === 'admin' || payload.role === 'salesperson'
            )
          })
      }

      dispatch(GetSettings())
    }
  }, [])

  useEffect(() => {
    // Ensure there's a history entry to go back to (important for Android back button
    // when users land directly on a venue page via QR code)
    if (window.history.length <= 1) {
      window.history.replaceState({ fromQR: true }, '', window.location.href)
      window.history.pushState(null, '', window.location.href)
    }
  }, [])

  useEffect(() => {
    if ((alias && venue?.data?.alias !== alias) || resId) {
      dispatch(GetVenue({ term: (alias || resId) }))
    }
  }, [alias, resId])

  useEffect(() => {
    if (venue.data.alias) {
      dispatch(GetProductTypes({
        venue: venue.data.alias,
        includeFavorites: true,
        isPackageOrder: !tableID
      })
      )
    }
  }, [venue.data.alias, tableID])

  useEffect(() => {
    if (tableID) {
      dispatch(GetTables({ alias }))
      setIsPackageOrder(false)
    } else setIsPackageOrder(true)
  }, [tableID])

  useEffect(() => {
    if (
      !venue.data ||
      (!isAdminOnline &&
        (venue.data.status === 'deactivated' ||
          venue.data.status === 'pending' ||
          venue.data.status === 'permanently_closed'))
    ) {
      navigate('/')
    } else if (
      venue.data?.firebaseUid &&
      settings.isLoaded &&
      !settings.isLoading
    ) {
      statusCheck(venue.data.firebaseUid)
    }
  }, [venue.data?.firebaseUid, settings.isLoaded])

  useEffect(async () => {
    if (venue.data?.design?.menu?.theme) { changeTheme(venue.data.design.menu.theme) }

    let userLocation = getUserLocation()

    if (venue.data?.deliveryAreas) {
      if (user.data?.addresses?.length > 0) {
        userLocation = user.data.addresses.find((a) => a._id === user.data.defaultAddress
        )
      } else userLocation = JSON.parse(localStorage.getItem('mylocation'))

      setFoundHotSaleDeliveryArea(
        venue.data.deliveryAreas.find(
          (deliveryArea) => deliveryArea.districts
            .reduce((acc, cur) => {
              acc = [...acc, ...cur.selectedFeatures]
              return acc
            }, [])
            .indexOf(userLocation?.h3Index) !== -1
        )
      )
    }

    if (venue.data?.coldSale?.isActive && !tableID) {
      setIsColdSaleActive(true)

      if (userLocation && venue.data?.alias) {
        setIsWithInColdSaleLimits(
          await CheckForColdSale({
            alias: venue.data.alias,
            to: userLocation?.h3Index
          })
        )
      }
    }

    if (venue.data?.hiddenDeliveryAreas) {
      setIsTemporarilyUnavailable(
        venue.data.hiddenDeliveryAreas.districts
          .reduce((acc, cur) => [...acc, ...cur.disabledFeatures], [])
          .includes(userLocation?.h3Index)
      )
    }

    return () => {
      userLocation = null
    }
  }, [venue.data?.design?.menu?.theme, venue.data?.deliveryAreas, venue.data?.coldSale?.isActive, venue.data?.alias, user.data.addresses
  ])

  useEffect(() => {
    if (Object.keys(table.data).length > 0) {
      // check if current table id is a valid table id
      const selectedTable = isTableAvailable()

      if (!selectedTable) {
        Swal.fire({
          title: 'Hata',
          text:
            'Belirttiğiniz masayı bulamadık. Paket sipariş sayfasına yönlendiriliyorsunuz.',
          icon: 'error',
          timer: 3000,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          customClass: {
            container: classes.themeFont
          },
          willClose: () => {
            navigate(`/${alias}`)
          }
        })
      } else dispatch(SetTable({ table: selectedTable }))
    }
  }, [table.isLoading])

  const getUserLocation = () => {
    if (user.data._id) {
      if (user.data.addresses.length > 0) {
        return user.data.addresses.find(
          (a) => a._id === user.data.defaultAddress
        )
      }
    } else return JSON.parse(localStorage.getItem('mylocation'))

    return null
  }

  const statusCheck = (firebaseUid) => {
    if (venue.data.status === 'active') {
      if (!settings.data.displayAllVenuesAsOpen && firebaseDatabase) {
        const myStatusRef = ref(firebaseDatabase, `onlineVenues/${firebaseUid}`)

        onValue(myStatusRef, (snapshot) => {
          setIsVenueOnline(!!snapshot.val())
        })
      } else {
        IsWorking({ alias }).then((result) => {
          setIsVenueOnline(result)
        })
      }
    } else setIsVenueOnline(false)
  }

  const isTableAvailable = () => {
    if (tableID === 'gelal' && table.takeawayTable) {
      return Object.keys(table.data)
        .reduce((acc, cur) => {
          acc = [...acc, ...table.data[cur].filter((d) => !d.isShape)]
          return acc
        }, [])
        .find((t) => t._id === table.takeawayTable)
    }
    const allTables = Object.keys(table.data).reduce((acc, cur) => {
      acc = [...acc, ...table.data[cur]]
      return acc
    }, [])

    for (let i = 0; i < allTables.length; i++) {
      if (allTables[i]._id === tableID) return allTables[i]
    }

    return false
  }

  if (!alias && !resId) return <Navigate to="./" replace />

  if (venue.isLoaded && productTypes.isLoaded) {
    return (
      <MenuContext.Provider
        value={{
          isVenueOnline,
          isPackageOrder,
          tableID,
          isColdSaleActive,
          isWithInColdSaleLimits,
          isTemporarilyUnavailable,
          foundHotSaleDeliveryArea,
          productTypes,
          isAdminOnline
        }}
      >
        <div style={{ backgroundColor: 'white' }}>
          <Topbar
            title={venue.data.name}
            type="menu"
            isPackageOrder={isPackageOrder}
            position="sticky"
          />
          <Suspense fallback={<Skeleton animation="wave" />}>
            <MenuContainer />
          </Suspense>
        </div>
      </MenuContext.Provider>
    )
  }
  return (
    <Backdrop className={classes.backdrop} open>
      <div>
        <CircularProgress color="inherit" />
        <p> Yükleniyor lütfen bekleyin...</p>
      </div>
    </Backdrop>
  )
}
export default Main
